import { FC, useEffect, useState } from 'react';
import styles from './styles/GoodsClassifierItemsPluginView.module.scss'
import { LoadingStatus } from '../../../@types/enumsGlobal';
import { DefaultGrid } from '../../../components/grids/default/defaultGrid';
import { PluginWrapper, ToolbarWrapper } from '../../../components/plugins';
import useGridFilter, { CreateSelectorGridFilter } from '../../../system/hooks/useGridFilter';
import { useAppContext } from '../../../system/providers/appContextProvider';
import { usePluginContext } from '../../../system/providers/plugin';
import { useTabsContext } from '../../../system/providers/tabsProvider';
import { Spinner } from '../../../components/spiner/Spinner';
import { GoodsClassifierProvider } from '../../../Services/DataProviders/GoodsClassifierDataProvider';
import { IGoodsClassifierViewDTO } from '../../../libs/coreapi-dto/dirs/goodsClassifier';
import DefaultDictionaryCommandsPanel from '../../../components/commandsPanels/DefaultDictionaryCommandsPanel';
import { GoodsClassifierCreatorModal } from './components/GoodsClassifierCreatorModal';

const GoodsClassifierGrid: FC<IGridProps> = (props) => {
    const appCtx = useAppContext();
    const pluginCtx = usePluginContext();
    const tabCtx = useTabsContext();
    const goodsClassifierDataProvider = new GoodsClassifierProvider(appCtx.coreApiService);
    const [data, setData] = useState<IGoodsClassifierViewDTO[]>([]);
    
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.InProcess);
    const [totalCount, setTotalCount] = useState(0);
    const [viewState, setViewState] = useState<GridStateType>('view');
    const [gridFilter, dispatchGridFilter] = useGridFilter(CreateSelectorGridFilter);
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>(props.data ?? []);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);

    const handleItemsChange = (newItems: IGridRow[]) => {
        setSelectedItems(newItems);
        props?.onMultipleSelect?.(newItems);
    };
    
    useEffect(() => {
            setIsSubmitting(true);
            setSelectedItem(undefined);
            goodsClassifierDataProvider.getView(gridFilter, async (data, totalCount) => {
                setData(data);
                setTotalCount(totalCount);
                setLoadingStatus(LoadingStatus.Completed);
                setIsSubmitting(false);
            })
        }, [gridFilter])
    
        useEffect(() => {
            if (viewState === 'refresh') {
                setIsSubmitting(true);
                goodsClassifierDataProvider.getView(gridFilter, async (data, totalCount) => {
                    setData(data);
                    setViewState('view');
                    setTotalCount(totalCount);
                    setLoadingStatus(LoadingStatus.Completed);
                    setIsSubmitting(false);
                })
            }
        }, [viewState])

    useEffect(() => {
        pluginCtx.masterGrid.onUnselectEvent();
    }, [tabCtx.currentTab]);

    useEffect(() => {
        if (props.dataFromPluginGridSelectorInput) {
            setSelectedItems(props.dataFromPluginGridSelectorInput);
        }
    }, [props.dataFromPluginGridSelectorInput]);

    return (
        <PluginWrapper>
            <ToolbarWrapper className={styles.toolBarWrapper}>
                <DefaultDictionaryCommandsPanel
                    selectedItem={[selectedItem, setSelectedItem]}
                    gridFilter={[gridFilter, dispatchGridFilter]}
                    dataProvider={goodsClassifierDataProvider}
                    creatorModalJsx={GoodsClassifierCreatorModal}
                    pluginSettings={props.plugin}
                    setViewState={(vs) => setViewState(vs)}
                    popOverReport={{
                        items: props.dataFromPluginGridSelectorInput ? selectedItems : [],
                        onItemsChange: handleItemsChange
                    }}
                />
            </ToolbarWrapper>
            {
                isSubmitting ? (
                    <Spinner />
                ) : (
                    <>
                        <DefaultGrid
                            showQuantity
                            gridId={props.gridId}
                            data={data}
                            loadingStatus={loadingStatus}
                            setLoadingStatus={setLoadingStatus}
                            separator
                            singleDirectory
                            totalCount={totalCount}
                            filter={gridFilter}
                            dataProvider={goodsClassifierDataProvider}
                            plugin={props.plugin}
                            selectedItem={selectedItem}
                            selectedItems={selectedItems}
                            multipleSelect={props.multipleSelect}
                            onDoubleClick={(row) => props.onDoubleClick?.(row)}
                            onSelect={(row) => {
                                setSelectedItem(row);
                                props?.onSelect?.(
                                    row,
                                    data.find((x) => x.idGlobal === row?.idGlobal)
                                );
                            }}
                            onMultipleSelect={(rows) => {
                                setSelectedItems(rows)
                                props?.onMultipleSelect?.(rows)
                            }}
                            onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                            onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                            onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                            onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                            onEnter={() => setViewState("edit")}
                        />
                        
                    </>
                )
            }
        </PluginWrapper>
    )
};

export default GoodsClassifierGrid;
